/*LIBRARY*/
import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { Reorder } from "framer-motion";

/*COMPONENT*/
import Modal from "../../Components/common_modal/Modal";
import HomeButton from "../../Components/form/HomeButton";
import UploadBanner from "../../Components/form/UploadBanner";
import UploadSquare from "../../Components/form/UploadSquare";
import EditGeneral from "../../Components/form/EditGeneral";
import EditTerm from "../../Components/form/EditTerm";
import FieldCard from "../../Components/form/FieldCard";
import NO_LICENSE_HANDLE from "../../Components/blocked_page/NO_LICENSE_HANDLE";
import EDIT_FIELD from "../../Components/editor_table/EDIT_FIELD";

/*REDUX*/
import {
  getDetailLayerByLinkWOgeojson,
  editFieldForm,
  deleteFieldForm,
  editAllFields,
} from "../../App/actions/layerActions";
import { is_has_license_basic_or_license_sharing } from "../../App/actions/filter_license";

/*PICTURE*/
import icon_edit_text from "../../Assets/svg/icon_edit_text.svg";
import icon_404 from "../../Assets/svg/icon_404.svg";

/*FUNCTION*/
import isEmpty from "../../App/validation/is-empty";

/*DATA*/
import dict from "../../Data/dict.json";
import domain_list from "../../Data/domain_list";

/*CONST*/

const CreateForm = ({ match }) => {
  const dispatch = useDispatch();

  // Redux Selectors
  const auth = useSelector((state) => state.auth);
  const layer = useSelector((state) => state.layer);

  // Local State
  const [modal_edit_field, set_modal_edit_field] = useState(false);
  const [modal_push_field, set_modal_push_field] = useState(false);
  const [modal_delete_field, set_modal_delete_field] = useState(false);
  const [modal_reorder, set_modal_reorder] = useState(false);
  const [modal_edit_general, set_modal_edit_general] = useState(false);
  const [modalTerm, set_modalTerm] = useState(false);
  const [field, set_field] = useState({});
  const [field_list, set_field_list] = useState([]);
  const [from_uuid, set_from_uuid] = useState("");
  const [to_uuid, set_to_uuid] = useState("");
  const [mode_middle, set_mode_middle] = useState(false);
  const [is_hold, set_is_hold] = useState(false);
  const [geo_layer, set_geo_layer] = useState({});

  // Refs for tracking previous props/state for comparison
  const prevLayerRef = useRef(layer);
  const prevSidebarWidthRef = useRef(0);
  const sidebar_width = 0; // Was state in original, but never updated. Kept as const.

  /* LIFECYCLE: ComponentDidMount */
  useEffect(() => {
    if (document.getElementById("container_document")) {
      document.getElementById("container_document").style.width = "100%";
    }
    if (document.getElementById("container_document")) {
      document.getElementById("container_document").style.marginLeft = "0px";
    }
    const { geo_layer_link } = match.params;
    dispatch(getDetailLayerByLinkWOgeojson(geo_layer_link));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* LIFECYCLE: ComponentDidUpdate Logic */
  useEffect(() => {
    const prevLayer = prevLayerRef.current;

    // Logic: Status layer update changed
    if (prevLayer.status_layer_update !== layer.status_layer_update) {
      const fields = layer?.geo_layer_wo_geojson?.geo_layer?.fields || [];
      const new_field_list = fields.filter(
        (item) => !item.parent_uuid && !item.isStyle,
      );
      set_field_list(new_field_list);
    }

    // Logic: geo_layer_id changed
    const currentId = layer?.geo_layer_wo_geojson?.geo_layer?._id;
    const prevId = prevLayer?.geo_layer_wo_geojson?.geo_layer?._id;

    if (currentId !== prevId) {
      set_geo_layer(layer.geo_layer_wo_geojson);

      const fields = layer?.geo_layer_wo_geojson?.geo_layer?.fields || [];
      const prev_fields =
        prevLayer?.geo_layer_wo_geojson?.geo_layer?.fields || [];

      if (fields.length > prev_fields.length) {
        if (modal_edit_field) {
          close_nav();
        }
      }
    }

    // Logic: Sidebar resize (simulated as sidebar_width is 0 but logic preserved)
    const new_width = sidebar_width;
    const old_width = prevSidebarWidthRef.current;

    if (
      new_width !== old_width &&
      document.getElementById("container_document")
    ) {
      document.getElementById("container_document").style.width =
        `calc(( 100% + ${sidebar_width * 1.3}px ))`;
    }

    // Update refs
    prevLayerRef.current = layer;
    prevSidebarWidthRef.current = sidebar_width;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, modal_edit_field]);

  /* FUNCTIONS */

  const submit_delete_picture_field = (target_field) => {
    // Deep copy to avoid direct mutation of state before dispatch
    let current_geo_layer_data = JSON.parse(JSON.stringify(geo_layer));
    let target_layer = current_geo_layer_data.geo_layer;

    const field_copy = { ...target_field };
    delete field_copy.picture_url;

    const body = {
      geo_layer_id: target_layer._id,
      field: field_copy,
    };
    dispatch(editFieldForm(body));
  };

  const toggle_is_hold = (value) => {
    set_is_hold(value);
  };

  const toggle_term = (target_field) => {
    set_modalTerm(!modalTerm);

    if (!isEmpty(target_field)) {
      const field_copy = { ...target_field };
      const { array_templates } = field_copy;
      const min = field_copy.min !== undefined ? field_copy.min : 0;
      const max = field_copy.max !== undefined ? field_copy.max : 10;
      const step = field_copy.step !== undefined ? field_copy.step : 1;

      const tagArray = array_templates.map((value) => {
        return value.name;
      });
      const tag = tagArray.join("\n");

      field_copy.tag = tag;
      field_copy.min = min;
      field_copy.max = max;
      field_copy.step = step;

      set_field(field_copy);
    }
  };

  const toggle_edit_general = () => {
    set_modal_edit_general(!modal_edit_general);
  };

  const toggle_reorder = (from_uuid_val, modal_reorder_value) => {
    set_modal_reorder(modal_reorder_value || !modal_reorder);
    set_from_uuid(from_uuid_val);
  };

  const submit_reorder = useCallback(
    (target_to_uuid) => {
      set_modal_reorder(!modal_reorder);
      set_to_uuid(target_to_uuid);

      // Logic moved inside to ensure access to latest state values if needed,
      // though in functional components with closures we use the values passed or current scope.
      let current_geo_layer = JSON.parse(
        JSON.stringify(layer.geo_layer_wo_geojson),
      );
      let fields = current_geo_layer?.geo_layer?.fields || [];

      if (fields.length > 0) {
        const from = fields.findIndex((f) => f?.uuid === from_uuid);
        const to = fields.findIndex((f) => f?.uuid === target_to_uuid);

        if (from !== -1 && to !== -1) {
          fields.splice(to, 0, fields.splice(from, 1)[0]);
          current_geo_layer.geo_layer.fields = fields;

          set_geo_layer(current_geo_layer);
          set_to_uuid("");

          const body = {
            geo_layer_id: current_geo_layer.geo_layer._id,
            fields: fields,
          };
          dispatch(editAllFields(body));
        }
      }
    },
    [layer.geo_layer_wo_geojson, from_uuid, modal_reorder, dispatch],
  );

  const run_submit_reorder_when_mouse_release = () => {
    // ketika reorder terjadi (untuk update ke database)
    const new_state_fields = field_list || [];
    if (new_state_fields.length > 0) {
      const fields_ori = layer?.geo_layer_wo_geojson?.geo_layer?.fields;
      const old_state_fields = fields_ori?.filter(
        (f) => !f.parent_uuid && !f.isStyle,
      );
      const index = new_state_fields.findIndex((f) => f.uuid === from_uuid);

      if (index !== -1 && old_state_fields[index]) {
        const target_to_uuid = old_state_fields[index].uuid;
        submit_reorder(target_to_uuid);
        set_modal_reorder(false);
      }
    }
  };

  const toggle_push_in_the_middle = (uuid) => {
    set_mode_middle(true);
    set_to_uuid(uuid);
    set_modal_push_field(!modal_push_field);
  };

  const toggle_delete_field = (target_field) => {
    set_modal_delete_field(!modal_delete_field);
    if (!isEmpty(target_field)) {
      set_field(target_field);
    }
  };

  const toggle_push_field = (parent_uuid_val) => {
    set_mode_middle(false);
    set_modal_push_field(!modal_push_field);

    if (parent_uuid_val) {
      set_field((prev) => ({ ...prev, parent_uuid: parent_uuid_val }));
    } else {
      set_field((prev) => ({ ...prev, parent_uuid: "" }));
    }
  };

  //Open sidebar
  const open_nav = (target_field) => {
    set_modal_edit_field(true);
    set_field(target_field);

    if (document.getElementById("container_document")) {
      document.getElementById("container_document").style.width =
        `calc(( 100% - ${sidebar_width}px ))`;
    }
    if (document.getElementById("container_document")) {
      document.getElementById("container_document").style.marginLeft =
        `${sidebar_width}px`;
    }
  };

  //Close sidebar
  const close_nav = () => {
    set_modal_edit_field(false);
    set_field({});

    if (document.getElementById("container_document")) {
      document.getElementById("container_document").style.width = "100%";
    }
    if (document.getElementById("container_document")) {
      document.getElementById("container_document").style.marginLeft = "0px";
    }
  };

  //SideBar action
  const toggle_sidebar = (target_field) => {
    if (!isEmpty(target_field) && target_field?.key !== field?.key) {
      const field_copy = { ...target_field };
      const { array_templates } = field_copy;
      const min = field_copy.min !== undefined ? field_copy.min : 0;
      const max = field_copy.max !== undefined ? field_copy.max : 10;
      const step = field_copy.step !== undefined ? field_copy.step : 1;

      const tagArray = array_templates.map((value) => {
        return value.name;
      });
      const tag = tagArray.join("\n");

      field_copy.tag = tag;
      field_copy.min = min;
      field_copy.max = max;
      field_copy.step = step;

      open_nav(field_copy);
    } else {
      const new_modal_state = !modal_edit_field;
      set_modal_edit_field(new_modal_state);
      new_modal_state ? open_nav(target_field) : close_nav();
    }
  };

  const handle_delete_field = async () => {
    set_modal_delete_field(false);

    const { geo_layer_wo_geojson } = layer;
    const field_key = field.key;
    const body = {
      geo_layer_id: geo_layer_wo_geojson?.geo_layer?._id,
      field_key,
    };

    await dispatch(deleteFieldForm(body));

    set_field_list(
      layer?.geo_layer_wo_geojson?.geo_layer?.fields?.filter(
        (f) => !f.parent_uuid && !f.isStyle,
      ),
    );
  };

  /* RENDER HELPERS */

  //local storage
  const language = localStorage?.language || "ina";

  //global props
  const { domain, isAuthenticated } = auth;
  const { loadingProcess, itemLoading } = layer;

  const parent_uuid = field?.parent_uuid || "";

  // Note: using local state geo_layer usually, but strictly following original render logic which derived from props often
  // original: let geo_layer = this.props.layer.geo_layer_wo_geojson;
  let current_geo_layer = layer.geo_layer_wo_geojson;
  current_geo_layer = current_geo_layer?.geo_layer;

  //content
  let modal_push_field_content,
    modal_delete_fieldContent,
    modal_edit_generalContent,
    modalTermContent;

  const domain_temp = domain_list?.[domain]?.form_link;

  const modal_edit_field_content = modal_edit_field && (
    <Modal
      modalSize="medium"
      id="modal"
      isOpen={modal_edit_field}
      onClose={toggle_sidebar}
      title="Edit Kolom"
    >
      <div className="box-body" id="box-body">
        <EDIT_FIELD
          from="form"
          field={field}
          toggleEditField={toggle_sidebar}
          parent_uuid={parent_uuid}
        />
      </div>
    </Modal>
  );

  let final_content = (
    <main style={{ textAlign: "center", marginTop: "100px" }}>
      <section>
        <img
          src={icon_404}
          alt="Viewer"
          style={{ width: "200px", maxWidth: "70%", marginBottom: "20px" }}
        />
      </section>
      <section style={{ fontSize: "15px" }}>
        {dict?.["Loading GIS data"]?.[language]}...
      </section>
    </main>
  );

  if (loadingProcess && itemLoading === "loading_layer_wo_geojson") {
    final_content = (
      <main style={{ textAlign: "center", marginTop: "100px" }}>
        <section>
          <img
            src={icon_404}
            alt="Viewer"
            style={{ width: "200px", maxWidth: "70%", marginBottom: "20px" }}
          />
        </section>
        <section style={{ fontSize: "15px" }}>
          {dict?.["Loading GIS data"]?.[language]}...
        </section>
      </main>
    );
  } else {
    let item_content;

    if (!isEmpty(current_geo_layer)) {
      item_content = (
        <Reorder.Group axis="y" values={field_list} onReorder={set_field_list}>
          {field_list.map((field_element, idx) => {
            return (
              <FieldCard
                key={field_element.uuid}
                idx={idx}
                field_element={field_element}
                fields={field_list}
                field={field}
                from_uuid={from_uuid}
                modal_reorder={modal_reorder}
                language={language}
                toggle_delete_field={toggle_delete_field}
                toggle_reorder={toggle_reorder}
                submit_reorder={submit_reorder}
                toggle_term={toggle_term}
                toggle_push_field={toggle_push_field}
                toggle_sidebar={toggle_sidebar}
                submit_delete_picture_field={submit_delete_picture_field}
                setFromUuid={set_from_uuid}
                toggle_push_in_the_middle={toggle_push_in_the_middle}
                run_submit_reorder_when_mouse_release={
                  run_submit_reorder_when_mouse_release
                }
                toggle_is_hold={toggle_is_hold}
                is_hold={is_hold}
              />
            );
          })}
        </Reorder.Group>
      );
    }

    let loadingText;
    switch (itemLoading) {
      case "editField":
        loadingText = dict["Saving changes on a question"][language];
        break;
      case "upload_photo_form":
        loadingText = dict["Uploading banner"][language];
        break;
      case "loading_layer_wo_geojson":
        loadingText = dict["Load the data"][language];
        break;
      case "deleteFieldForm":
        loadingText = "Deleting question";
        break;
      default:
        loadingText = dict["Saving changes on a question"][language];
        break;
    }

    modalTermContent = modalTerm && (
      <Modal
        modalSize="medium"
        id="modal"
        isOpen={modalTerm}
        onClose={toggle_term}
      >
        <main className="box-body" style={{ minHeight: "75vh" }}>
          <EditTerm field={{ ...field }} fields={[...field_list]} />
        </main>
      </Modal>
    );

    modal_push_field_content = modal_push_field && (
      <Modal
        modalSize="medium"
        id="modal"
        isOpen={modal_push_field}
        onClose={toggle_push_field}
        title="Tambah Kolom"
      >
        <div className="box-body" id="box-body">
          <EDIT_FIELD
            from="form"
            mode_middle={mode_middle}
            to_uuid={to_uuid}
            togglePushField={toggle_push_field}
            parent_uuid={parent_uuid}
          />
        </div>
      </Modal>
    );

    modal_delete_fieldContent = modal_delete_field && (
      <Modal
        modalSize="small"
        id="modal"
        isOpen={modal_delete_field}
        onClose={toggle_delete_field}
        title="Konfirmasi hapus"
      >
        <div className="box-body">
          <div className="text_bold margin_bottom_extra">
            {dict["Are you sure to delete"][language]} {field.name}
          </div>
          <button onClick={handle_delete_field} className="button hover_danger">
            {dict["Delete"][language]}
          </button>
        </div>
      </Modal>
    );

    modal_edit_generalContent = modal_edit_general && (
      <Modal
        modalSize="large"
        id="modal"
        isOpen={modal_edit_general}
        onClose={toggle_edit_general}
      >
        <main className="box-body" style={{ textAlign: "center" }}>
          <EditGeneral
            geo_layer={current_geo_layer}
            toggle_edit_general={toggle_edit_general}
          />
        </main>
      </Modal>
    );

    const loading_content = loadingProcess && (
      <section
        style={{
          position: "fixed",
          top: "0px",
          width: "100%",
          height: "20px",
          zIndex: "9",
          backgroundColor: "#2196f3",
          color: "#fff",
          textAlign: "center",
        }}
      >
        {loadingText}
      </section>
    );

    const add_button = (
      <main
        onClick={() => toggle_push_field()}
        style={{
          position: "sticky",
          width: "85px",
          bottom: "25px",
          margin: "auto",
          borderRadius: "10px",
          cursor: "pointer",
          textAlign: "center",
          padding: "7px",
        }}
        data-mapid="clickTambahPertanyaan"
        className="background_black"
      >
        <section
          style={{
            fontSize: "30px",
          }}
        >
          +
        </section>
        <section className="text_inferior">
          {dict["Add Question"][language]}
        </section>
      </main>
    );

    final_content = (
      <main>
        <HomeButton />
        <main
          id="container_document"
          style={{
            transition: "all 0.15s ease-in-out",
            marginTop: "55px",
          }}
        >
          <main className="container_document">
            <UploadBanner />
            <div
              className="container_flat"
              style={{
                backgroundColor: "#fff",
                marginTop: "10px",
                marginBottom: "10px",
              }}
            >
              <div className="text_header">
                {current_geo_layer?.name
                  ? current_geo_layer?.name
                  : dict["Loading"][language]}{" "}
                <img
                  onClick={toggle_edit_general}
                  style={{ cursor: "pointer" }}
                  src={icon_edit_text}
                  alt="Edit general"
                  width="15px"
                  height="auto"
                  data-mapid="clickEdit"
                />
              </div>
              <div className="text_medium sub_color">
                {current_geo_layer?.description
                  ? current_geo_layer?.description
                  : "Form Description"}
              </div>

              <a
                href={`${domain_temp}${current_geo_layer?.link}`}
                target="_blank"
                rel="noopener noreferrer"
                className="button background_black"
                style={{ marginRight: "10px", marginTop: "10px" }}
              >
                {dict["Link"][language]}
              </a>
            </div>
            <div
              className="container_flat"
              style={{
                backgroundColor: "#fff",
                marginTop: "10px",
                marginBottom: "10px",
                padding: "30px",
              }}
            >
              <UploadSquare />
            </div>
            <div style={{ paddingBottom: "200px" }}>{item_content}</div>
            {add_button}
          </main>
        </main>
        {loading_content}
      </main>
    );
  }

  let content = null;
  // Note: Assuming is_has_license_basic_or_license_sharing is a selector or helper available
  // Original props mapping suggests it might be an action creator, but it's used as a boolean check in render.
  // If it is a function from actions, in functional we usually dispatch, but here it's used for condition.
  // Assuming it returns boolean based on state or is a helper imported.
  // Since it was mapped in connect, it's likely an action, but used as logic?
  // If it's a pure helper function:
  if (!is_has_license_basic_or_license_sharing() || !isAuthenticated) {
    content = <NO_LICENSE_HANDLE />;
  } else {
    content = (
      <main
        onMouseUp={() => {
          if (is_hold) {
            run_submit_reorder_when_mouse_release();
            toggle_is_hold(false);
          }
        }}
      >
        {modal_push_field_content}
        {modal_delete_fieldContent}
        {modal_edit_generalContent}
        {modalTermContent}

        {final_content}
        <Helmet>
          <title>
            {current_geo_layer?.name
              ? current_geo_layer?.name
              : dict["Loading"][language]}
          </title>
          <meta
            name="description"
            content={
              current_geo_layer?.name
                ? current_geo_layer?.name
                : dict["Loading"][language]
            }
          />
        </Helmet>
        <style>
          {`
        body{
          padding-top: 0px;
          overflow-y: auto;
        }
        #navbarDefault{ visibility: none;}
        
        `}
        </style>
      </main>
    );
  }

  return (
    <main style={{ backgroundColor: "#e4ecf1" }}>
      {content}
      {modal_edit_field_content}
    </main>
  );
};

export default CreateForm;
